import React, { useState, useEffect, useRef } from "react";
import { Button, message } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { GiftOutlined, HomeOutlined, ShoppingCartOutlined } from "@ant-design/icons";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import "../styles/global-background.css";
import "../styles/OpenBoxPage.css";
import "../styles/cards/index.css";

// 皮肤数据接口
interface SkinData {
  id: string;
  name: string;
  cardFront: string;
  cardBack: string;
  rarity: string;
  description: string;
}

const OpenBoxPage: React.FC = () => {
  const [language, setLanguage] = useState<"en" | "zh">("zh");
  const [isOpening, setIsOpening] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [revealedSkin, setRevealedSkin] = useState<SkinData | null>(null);
  const [showCard, setShowCard] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const cardRef = useRef<HTMLDivElement | null>(null);

  // 皮肤数据 - 修正文件路径
  const skinData: SkinData[] = [
    {
      id: "skin-machao",
      name: "马超 - 神威天将军",
      cardFront: "/images/skinCards/machao_1.webp",
      cardBack: "/images/skinCards/four_skin_cardback.webp",
      rarity: "epic",
      description: "马超的限定神威天将军皮肤"
    },
    {
      id: "skin-lvmeng",
      name: "吕蒙 - 白衣渡江",
      cardFront: "/images/skinCards/lvmeng_1.webp",
      cardBack: "/images/skinCards/four_skin_cardback.webp",
      rarity: "epic",
      description: "吕蒙的限定白衣渡江皮肤"
    },
    {
      id: "skin-liubei",
      name: "刘备 - 仁德之主",
      cardFront: "/images/skinCards/liubei_1.webp",
      cardBack: "/images/skinCards/four_skin_cardback.webp",
      rarity: "legendary",
      description: "刘备的限定仁德之主皮肤"
    },
    {
      id: "skin-huangyueying",
      name: "黄月英 - 机关巧匠",
      cardFront: "/images/skinCards/huangyueying_1.webp",
      cardBack: "/images/skinCards/four_skin_cardback.webp",
      rarity: "rare",
      description: "黄月英的限定机关巧匠皮肤"
    },
    {
      id: "skin-ganfuren",
      name: "甘夫人 - 昭烈皇后",
      cardFront: "/images/skinCards/ganfuren_1.webp",
      cardBack: "/images/skinCards/four_skin_cardback.webp",
      rarity: "epic",
      description: "甘夫人的限定昭烈皇后皮肤"
    },
    {
      id: "skin-zhaoyun",
      name: "赵云 - 龙胆亮银枪",
      cardFront: "/images/skinCards/zhaoyun_1.webp",
      cardBack: "/images/skinCards/four_skin_cardback.webp",
      rarity: "legendary",
      description: "赵云的限定龙胆亮银枪皮肤"
    }
  ];

  // 获取稀有度颜色
  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'common': return '#7f7f7f';
      case 'uncommon': return '#1ab34a';
      case 'rare': return '#0070dd';
      case 'epic': return '#a335ee';
      case 'legendary': return '#ff8000';
      default: return '#7f7f7f';
    }
  };

  // 获取稀有度文本
  const getRarityText = (rarity: string) => {
    const rarityMap: Record<string, string> = {
      'common': '普通',
      'uncommon': '优秀',
      'rare': '稀有',
      'epic': '史诗',
      'legendary': '传说'
    };
    return rarityMap[rarity] || rarity;
  };

  // 开盒动画
  const handleOpenBox = () => {
    if (isOpening || hasOpened) return;

    setIsOpening(true);

    // 模拟开盒过程
    setTimeout(() => {
      // 随机选择一个皮肤
      const randomSkin = skinData[Math.floor(Math.random() * skinData.length)];
      setRevealedSkin(randomSkin);
      setIsOpening(false);

      // 延迟显示卡牌
      setTimeout(() => {
        setShowCard(true);
        setHasOpened(true);
        message.success(`恭喜获得 ${randomSkin.name}！`);
      }, 500);
    }, 3000);
  };

  // 卡牌交互处理
  const handleCardInteraction = (e: React.MouseEvent<HTMLDivElement>) => {
    const card = cardRef.current;
    if (!card) return;

    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const posX = Math.max(0, Math.min(100, (x / rect.width) * 100));
    const posY = Math.max(0, Math.min(100, (y / rect.height) * 100));

    const centerX = posX - 50;
    const centerY = posY - 50;

    const fromCenter = Math.min(1, Math.sqrt(centerX * centerX + centerY * centerY) / 50);

    card.classList.add('active', 'interacting');
    card.style.setProperty('--pointer-x', `${posX}%`);
    card.style.setProperty('--pointer-y', `${posY}%`);
    card.style.setProperty('--pointer-from-center', fromCenter.toString());
    card.style.setProperty('--rotate-x', `${-(centerX / 3.5)}deg`);
    card.style.setProperty('--rotate-y', `${centerY / 2}deg`);
    card.style.setProperty('--card-opacity', '1');
  };

  const handleCardLeave = () => {
    const card = cardRef.current;
    if (!card) return;

    card.classList.remove('active', 'interacting');
    setTimeout(() => {
      card.style.setProperty('--pointer-x', '50%');
      card.style.setProperty('--pointer-y', '50%');
      card.style.setProperty('--pointer-from-center', '0');
      card.style.setProperty('--rotate-x', '0deg');
      card.style.setProperty('--rotate-y', '0deg');
      card.style.setProperty('--card-opacity', '0');
    }, 100);
  };

  return (
    <div className="nova-container open-box-page">
      <div className="nova-grid"></div>

      <Navbar language={language} />

      <div className="content">
        <div className="open-box-container">
          {!hasOpened ? (
            <>
              <div className="open-box-title">
                <h1 className="nova-title">
                  <GiftOutlined style={{ marginRight: '0.5rem' }} />
                  NOVA-fourmeme 联名盲盒
                </h1>
                <p className="nova-subtitle">
                  点击开启您的专属皮肤卡牌
                </p>
              </div>

              <div className="box-container">
                <div className={`mystery-box ${isOpening ? 'opening' : ''}`}>
                  {isOpening ? (
                    <div className="opening-animation">
                      <div className="particle"></div>
                      <div className="particle"></div>
                      <div className="particle"></div>
                      <div className="particle"></div>
                      <div className="particle"></div>
                      <div className="glow-effect"></div>
                      <div className="opening-text">开启中...</div>
                    </div>
                  ) : (
                    <div className="box-content">
                      <div className="dynamic-blindbox-preview">
                        <div className="blindbox-emoji">📦</div>
                        <div className="blindbox-glow"></div>
                      </div>
                      <Button
                        type="primary"
                        size="large"
                        icon={<GiftOutlined />}
                        className="open-button"
                        onClick={handleOpenBox}
                        disabled={isOpening}
                      >
                        开启盲盒
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="reveal-container">
              <div className="reveal-title">
                <h1 className="nova-title congratulations">
                  🎉 恭喜获得稀有皮肤！
                </h1>
                <p className="nova-subtitle">
                  您获得了以下皮肤卡牌
                </p>
              </div>

              {revealedSkin && (
                <div className={`revealed-card-container ${showCard ? 'show' : ''}`}>
                  <div className="skin-info-display">
                    <h2 className="skin-name">{revealedSkin.name}</h2>
                    <div
                      className="skin-rarity"
                      style={{ color: getRarityColor(revealedSkin.rarity) }}
                    >
                      {getRarityText(revealedSkin.rarity)}
                    </div>
                    <p className="skin-description">{revealedSkin.description}</p>
                  </div>

                  <div
                    ref={cardRef}
                    className="card revealed-card"
                    data-rarity={revealedSkin.rarity.toLowerCase()}
                    data-subtypes="skin"
                    data-supertype="cosmetic"
                    onMouseMove={handleCardInteraction}
                    onMouseLeave={handleCardLeave}
                  >
                    <div className="card__translater">
                      <div className="card__rotator">
                        <img
                          className="card__back"
                          src={revealedSkin.cardBack}
                          alt="Card back"
                          loading="lazy"
                          width="660"
                          height="921"
                        />
                        <div className="card__front">
                          <img
                            src={revealedSkin.cardFront}
                            alt={revealedSkin.name}
                            className="card__artwork"
                            loading="lazy"
                            width="660"
                            height="921"
                          />
                          <span className="card__name">{revealedSkin.name}</span>
                          <div className="card__shine"></div>
                          <div className="card__glare"></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="action-buttons">
                    <Button
                      size="large"
                      icon={<ShoppingCartOutlined />}
                      onClick={() => navigate('/market')}
                    >
                      继续购买
                    </Button>
                    <Button
                      type="primary"
                      size="large"
                      icon={<HomeOutlined />}
                      onClick={() => navigate('/')}
                    >
                      返回首页
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <Footer language={language} setLanguage={setLanguage} />
    </div>
  );
};

export default OpenBoxPage; 